import {
	Box,
	Flex,
	Avatar,
	HStack,
	IconButton,
	Button,
	Menu,
	MenuButton,
	MenuList,
	MenuItem,
	useColorModeValue,
	Image,
	Stack,
	useColorMode,
} from '@chakra-ui/react';
import { HamburgerIcon, CloseIcon, SunIcon, MoonIcon } from '@chakra-ui/icons';
import LogView from './logger/log_view';
import PlayerLogView from './logger/player_log_view';
import EmployeeLogView from './logger/employee_log_view';
import AddDeviceIdModal from './profile/add_device_id';
import { useState, useEffect } from 'react';
import RemoteConfig from './remote_config/remote_config_view';
import BuildsPage from './builds/build_view';
import AddressableView from './addressable/addressable_view';
import ABTestView from './ab_test/ab_test_view';
import { useDisclosure } from '@chakra-ui/hooks';
import SegmentationView from './segment/segment_view';
import SelectorView from './selector/selector_view';
import UpdatePasswordModal from './profile/UpdatePasswordModal';
import GetAlictusIdModal from './profile/convert_playfab_id_to_alictus_id';
import UserOverrideModal from './profile/user_override';
import RemoteConfigReadModal from './profile/read_remote_config_with_id';
import JsonDiffCheckerModal from './profile/json_diff_checker';
import usePeriodicJwtRefresh from './hooks/usePeriodicJwtRefreshHook';
import FetchRCByPlayFabModal from './remote_config/fetch_rc_by_playfab_id_modal';

export const ColorModeToggle = () => {
	const { toggleColorMode } = useColorMode();
	const SwitchIcon = useColorModeValue(MoonIcon, SunIcon);
	const label = useColorModeValue('Switch to dark mode', 'Switch to light mode');

	return (
		<IconButton
			size="md"
			fontSize="lg"
			variant="ghost"
			color="current"
			onClick={toggleColorMode}
			icon={<SwitchIcon />}
			aria-label={label}
		/>
	);
};

const Links = [
	'Live Logger',
	'Remote Config-PROD',
	'Remote Config-DEV',
	'Builds',
	'Addressables',
	'AB Test',
	'Segmentation',
	'Data Selectors',
	'Player Logger',
	'Internal Logger',
];

const NavLink = ({ children, setSelectedLink, isActive }) => {
	const activeBg = useColorModeValue('gray.300', 'gray.700');
	return (
		<Box
			as="a"
			px={2}
			py={1}
			rounded={'md'}
			bg={isActive ? activeBg : undefined} // Active background color
			color={isActive ? 'inherit' : undefined} // Active text color
			_hover={{
				textDecoration: 'none',
				bg: useColorModeValue('gray.200', 'gray.600'),
			}}
			href={'#'}
			onClick={() => setSelectedLink(children)}
		>
			{children}
		</Box>
	);
};

export function NavBar({ userToken, setUserToken, avatarUrl, roles }) {
	const { isOpen, onOpen, onClose } = useDisclosure();
	const addDeviceDisclosure = useDisclosure();
	const getAlictusIdDisclosure = useDisclosure();
	const userOverrideDisclosure = useDisclosure();
	const [selectedGameId, setSelectedGameId] = useState('com.ck.tripletiles');
	const [selectedLink, setSelectedLink] = useState('Live Logger');
	const [activeComponent, setActiveComponent] = useState('Live Logger');
	const updatePasswordDisclosure = useDisclosure();
	const readRemoteConfigWithIdDisclosure = useDisclosure();
	const jsonRemoteConfigReadDisclosure = useDisclosure();
	const fetchRCByPlayFabDisclosure = useDisclosure();
	usePeriodicJwtRefresh();

	// Check URL params on mount to set the active component
	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const view = params.get('view');
		
		if (view === 'player-logs') {
			setActiveComponent('Player Logger');
			setSelectedLink('Player Logger');
		}
	}, []);

	// Function to check if a user has a specific role
	const hasRole = (role) => roles.includes(role);

	const handleLogout = () => {
		try {
			setUserToken(null);
			sessionStorage.removeItem('dashboardUserInfo'); // Remove dashboardUserInfo from local storage
		} catch (error) {
			console.error('Logout error:', error);
		}
	};

	return (
		<>
			<Box bg={useColorModeValue('gray.100', 'gray.900')} px={4}>
				<Flex h={16} alignItems={'center'} justifyContent={'space-between'}>
					<IconButton
						size={'md'}
						icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
						aria-label={'Open Menu'}
						display={{ md: 'none' }}
						onClick={isOpen ? onClose : onOpen}
					/>
					<HStack spacing={8} alignItems={'center'}>
						<Box>
							<Image src="logo512.png" width={'40px'} />
						</Box>
						<HStack as={'nav'} spacing={4} display={{ base: 'none', md: 'flex' }}>
							{Links.map((link) => (
								<NavLink
									setSelectedLink={(link) => {
										// Clear URL parameters when switching views
										window.history.replaceState(null, '', window.location.pathname);
										setSelectedLink(link);
										setActiveComponent(link);
									}}
									key={link}
									isActive={selectedLink === link}
								>
									{link}
								</NavLink>
							))}
						</HStack>
					</HStack>
					<Flex alignItems={'center'} gap={4}>
						<ColorModeToggle />
						<Menu>
							<MenuButton as={Button} rounded={'full'} variant={'link'} cursor={'pointer'} minW={0}>
								<Avatar size={'sm'} src={avatarUrl} />
							</MenuButton>
							<MenuList>
								{addDeviceDisclosure.isOpen && (
									<AddDeviceIdModal isOpen={addDeviceDisclosure.isOpen} onClose={addDeviceDisclosure.onClose} />
								)}
								{updatePasswordDisclosure.isOpen && (
									<UpdatePasswordModal isOpen={updatePasswordDisclosure.isOpen} onClose={updatePasswordDisclosure.onClose} />
								)}
								{getAlictusIdDisclosure.isOpen && (
									<GetAlictusIdModal isOpen={getAlictusIdDisclosure.isOpen} onClose={getAlictusIdDisclosure.onClose} />
								)}
								{userOverrideDisclosure.isOpen && (
									<UserOverrideModal isOpen={userOverrideDisclosure.isOpen} onClose={userOverrideDisclosure.onClose} />
								)}
								{readRemoteConfigWithIdDisclosure.isOpen && (
									<RemoteConfigReadModal
										isOpen={readRemoteConfigWithIdDisclosure.isOpen}
										onClose={readRemoteConfigWithIdDisclosure.onClose}
									/>
								)}
								{jsonRemoteConfigReadDisclosure.isOpen && (
									<JsonDiffCheckerModal isOpen={jsonRemoteConfigReadDisclosure.isOpen} onClose={jsonRemoteConfigReadDisclosure.onClose} />
								)}
								{fetchRCByPlayFabDisclosure.isOpen && (
									<FetchRCByPlayFabModal isOpen={fetchRCByPlayFabDisclosure.isOpen} onClose={fetchRCByPlayFabDisclosure.onClose} />
								)}
								<MenuItem onClick={() => addDeviceDisclosure.onOpen()}>Add DeviceId</MenuItem>
								<MenuItem onClick={() => updatePasswordDisclosure.onOpen()}>Update Password</MenuItem>
								<MenuItem onClick={() => getAlictusIdDisclosure.onOpen()}>Get Playfab Id</MenuItem>
								<MenuItem onClick={() => userOverrideDisclosure.onOpen()}>User Override</MenuItem>
								{(hasRole('ADMIN') || hasRole('PRODUCT')) && (
									<MenuItem onClick={() => fetchRCByPlayFabDisclosure.onOpen()}>Fetch AB Test-RC by PlayFabId</MenuItem>
								)}
								{(hasRole('ADMIN') || hasRole('PRODUCT')) && (
									<MenuItem onClick={() => readRemoteConfigWithIdDisclosure.onOpen()}>Fetch Remote Config With ID</MenuItem>
								)}
								{(hasRole('ADMIN') || hasRole('PRODUCT')) && (
									<MenuItem onClick={() => jsonRemoteConfigReadDisclosure.onOpen()}>Json Diff Checker</MenuItem>
								)}
								<MenuItem onClick={handleLogout}>Logout</MenuItem>
							</MenuList>
						</Menu>
					</Flex>
				</Flex>

				<UpdatePasswordModal isOpen={updatePasswordDisclosure.isOpen} onClose={updatePasswordDisclosure.onClose} />

				{isOpen ? (
					<Box pb={4} display={{ md: 'none' }}>
						<Stack as={'nav'} spacing={4}>
							{Links.map((link) => (
								<NavLink 
									key={link}
									setSelectedLink={(link) => {
										// Clear URL parameters when switching views
										window.history.replaceState(null, '', window.location.pathname);
										setSelectedLink(link);
										setActiveComponent(link);
										onClose(); // Close mobile menu after selection
									}}
									isActive={selectedLink === link}
								>
									{link}
								</NavLink>
							))}
						</Stack>
					</Box>
				) : null}
			</Box>

			{/* Conditionally render components based on activeComponent and roles */}
			{activeComponent === 'Live Logger' && <LogView userToken={userToken} selectedGameId={selectedGameId} />}
			{activeComponent === 'Remote Config-PROD' && <RemoteConfig environment={'production'} header={'Remote Config - PROD'} />}
			{activeComponent === 'Remote Config-DEV' && <RemoteConfig environment={'development'} header={'Remote Config - DEV'} />}
			{activeComponent === 'AB Test' && <ABTestView userToken={userToken} selectedGameId={selectedGameId} />}
			{activeComponent === 'Segmentation' && <SegmentationView />}
			{activeComponent === 'Data Selectors' && <SelectorView />}
			{activeComponent === 'Builds' && <BuildsPage />}
			{activeComponent === 'Addressables' && <AddressableView userToken={userToken} selectedGameId={selectedGameId} />}
			{activeComponent === 'Player Logger' && <PlayerLogView userToken={userToken} selectedGameId={selectedGameId} />}
			{activeComponent === 'Internal Logger' && <EmployeeLogView userToken={userToken} selectedGameId={selectedGameId} />}
		</>
	);
}

export default NavBar;
